import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AutocompleteElement } from 'components/muiInputWrappers';
import { CustomBasicOptionsModal, FormTitle } from 'components/shared';
import ActionsButtons from 'components/shared/GenericModalV2/components/ActionsButtons';

import { StatusFlowByID, AlStatus, Payload, StatusSelected } from '../../types';
import useGetAllProblemStatus from '../../services/useGetStatus';

type Props = {
  handleAddState: (payload: Payload) => void;
  handleEditState: (payload: StatusFlowByID) => void;
  registerForEdit: StatusFlowByID;
  dataForTheTable: StatusFlowByID[];
  isBase: boolean;
};

type Options = {
  label: string;
  id: number;
}[];

function StateFlowSelection({
  handleAddState,
  handleEditState,
  registerForEdit,
  dataForTheTable,
  isBase
}: Props) {
  const form = useForm<{
    statusId: number;
  }>({
    defaultValues: {
      statusId: registerForEdit?.statusId ?? null
    },
    mode: 'all'
  });

  const { t } = useTranslation('global');
  const { data: statusList, isLoading: isStatusListLoading } =
    useGetAllProblemStatus({
      onlyEnabled: 'true'
    });

  const [statusCheckOptions, setStatusCheckOptions] = useState<AlStatus[]>([]);
  const [availableSelectOptions, setAvailableSelectOptions] = useState<Options>(
    []
  );
  const [isUncheckModalOpen, setOpenUncheckModal] = useState(false);
  const [uncheckStatusId, setUncheckStatusId] = useState<number | null>(null);

  const statusIdWatched = form.watch('statusId');

  // only edition
  useEffect(() => {
    if (registerForEdit && statusList?.length) {
      const statusSelectedForEdit = registerForEdit?.statusSelected?.map(
        (status) => ({
          ...status,
          checked: true
        })
      ) as StatusSelected[];

      const editStatusOptions = statusList
        ?.filter((status) => status.id !== registerForEdit.statusId)
        ?.map((status) => ({
          ...status,
          label: status?.name || '',
          checked: statusSelectedForEdit?.some((a) => a?.id === status.id)
        }));

      setStatusCheckOptions(editStatusOptions);
    }
  }, [registerForEdit, form, statusList]);

  // only edition
  useEffect(() => {
    if (!registerForEdit && statusIdWatched) {
      setStatusCheckOptions(
        statusList
          ?.filter((status) => status.id !== statusIdWatched)
          ?.map((status) => ({
            ...status,
            label: status?.name || '',
            checked: false
          }))
      );
    }
  }, [statusIdWatched, registerForEdit, statusList]);

  useEffect(() => {
    if (!statusList?.length) {
      setAvailableSelectOptions([]);
      return;
    }

    if (registerForEdit) {
      // En edición queda fija una sola opción
      setAvailableSelectOptions([
        {
          label: registerForEdit.name,
          id: registerForEdit.statusId
        }
      ]);
      return;
    }

    if (!dataForTheTable?.length) {
      // Si no hay datos, mostrar todos los estados disponibles
      setAvailableSelectOptions(
        statusList
          ?.filter((status) => status?.id != null)
          ?.map((status) => ({
            id: status.id,
            label: status.name
          })) || []
      );
      return;
    }

    const addedStatusIds = new Set<number>();
    const initialStatusIds = new Set<number>();
    const newAvailableSelectOptions: Options = [];

    dataForTheTable?.forEach((item) => {
      if (item?.statusId != null) {
        initialStatusIds.add(item.statusId);
      }

      item?.statusSelected?.forEach((status) => {
        if (status?.id == null || addedStatusIds.has(status.id)) return;

        addedStatusIds.add(status.id);
        newAvailableSelectOptions.push({
          id: status.id,
          label: status.label || ''
        });
      });
    });

    let filteredOptions = newAvailableSelectOptions.filter(
      (option) => !initialStatusIds.has(option.id)
    );

    if (!filteredOptions.length) {
      filteredOptions =
        statusList
          ?.filter(
            (status) => status?.id != null && !initialStatusIds.has(status.id)
          )
          ?.map((status) => ({
            id: status.id,
            label: status.name
          })) || [];
    }

    setAvailableSelectOptions(filteredOptions);
  }, [dataForTheTable, registerForEdit, statusList]);

  const closeUncheckModal = useCallback(() => {
    setOpenUncheckModal(false);
    setUncheckStatusId(null);
  }, []);

  const openUncheckModal = useCallback((statusId: number) => {
    setUncheckStatusId(statusId);
    setOpenUncheckModal(true);
  }, []);

  const handleChangeStatusSelection = (statusId: number) => {
    const updatedCheckOptions = statusCheckOptions?.map((status) => {
      if (status.id === statusId) {
        const matchingStatusIsChecked = dataForTheTable.some(
          (item) =>
            item.statusSelected?.some((sta) => sta.id === statusId) &&
            status.checked &&
            registerForEdit?.statusSelected?.some(
              (prevChecked) => prevChecked?.id === statusId
            )
        );

        if (matchingStatusIsChecked) {
          openUncheckModal(statusId);
          return status;
        }
        return {
          ...status,
          checked: !status.checked
        };
      }

      return status;
    });

    setStatusCheckOptions(updatedCheckOptions);
  };

  const handleSubmitUncheckModal = () => {
    if (uncheckStatusId) {
      const updatedCheckOptions = statusCheckOptions?.map((status) => {
        if (status.id === uncheckStatusId) {
          const newItem = {
            ...status,
            checked: false
          };
          return newItem;
        }
        return status;
      });

      setStatusCheckOptions(updatedCheckOptions);
      closeUncheckModal();
    }
  };

  const handleSubmit = () => {
    const statusSelected = statusCheckOptions.filter(
      (status) => status?.checked
    );

    const statusWatched = availableSelectOptions?.find(
      (status) => status?.id === statusIdWatched
    );

    const payload: StatusFlowByID = {
      name: statusWatched?.label,
      statusId: statusWatched?.id,
      base: registerForEdit?.base,
      statusNumberOfChanges: statusSelected?.length,
      statusSelected
    };

    if (registerForEdit?.base) payload.base = true;

    if (registerForEdit) handleEditState(payload);
    else handleAddState(payload);
  };

  return (
    <>
      <CustomBasicOptionsModal
        acceptSubmit={handleSubmitUncheckModal}
        btnAcceptText={t('common.commonActions.accept')}
        btnCancelText={t('common.commonActions.cancel')}
        cancelSubmit={closeUncheckModal}
        message={t('clients.statusAdded')}
        show={isUncheckModalOpen}
      />
      <Grid data-testid="state-selection-modal">
        <Grid>
          <div style={{ marginTop: 43, marginBottom: 15 }}>
            <FormTitle gutterBottom>
              {t('problems.statusFlows.nextStatus')}:
            </FormTitle>
            <AutocompleteElement
              autocompleteProps={{
                size: 'small',
                placeholder: 'Todos',
                defaultValue: [],
                sx: { width: '100%' }
              }}
              control={form?.control}
              disabled={!!registerForEdit || isStatusListLoading}
              handleClear={() => {
                setStatusCheckOptions([]);
                form.setValue('statusId', null);
              }}
              loading={isStatusListLoading}
              name="statusId"
              options={availableSelectOptions}
              rules={{
                minLength: {
                  value: 1,
                  message: t('ticket.messages.valueLessThan1')
                },
                maxLength: {
                  value: 30,
                  message: t('ticket.messages.valueLongerThan30')
                }
              }}
              matchId
            />
          </div>

          <Grid
            sx={{
              marginTop: 3.5,
              display: 'flex',
              flexDirection: 'column'
            }}
            container
          >
            <Grid justifyContent="space-between" container>
              <FormTitle gutterBottom>{t('clients.changes')}:</FormTitle>
            </Grid>

            <Grid height="490px" overflow="auto">
              {(statusCheckOptions || [])?.map((status) => (
                <Grid
                  border={1}
                  borderColor="divider"
                  justifyContent="space-between"
                  key={status.id}
                  container
                >
                  <FormControlLabel
                    checked={status.checked}
                    control={
                      <Checkbox
                        disabled={isBase}
                        onChange={() => handleChangeStatusSelection(status.id)}
                      />
                    }
                    label={t(status.label)}
                    name={t(status.label)}
                    style={{ marginLeft: 2 }}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
        <ActionsButtons
          acceptButtonTitle={t('common.commonActions.save')}
          displayCancelButton={false}
          justifyContentProp="flex-end"
          onAccept={handleSubmit}
        />
      </Grid>
    </>
  );
}

export default StateFlowSelection;
